import React from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Stethoscope,
  ClipboardList,
  Mail,
  Sparkles,
  Layers,
} from "lucide-react";

const steps = [
  {
    icon: MessageCircle,
    title: "Free consultation",
    body: "Send a quick WhatsApp and book a free, no-obligation consultation with Dr. Patel — usually same-week availability.",
  },
  {
    icon: Stethoscope,
    title: "Clinical assessment",
    body: "Dr. Patel examines your jaw muscles, bite, tooth wear, TMJ and how freely you can open your mouth — and listens to what's been bothering you.",
  },
  {
    icon: ClipboardList,
    title: "Personal plan made",
    body: "Based on your assessment, we put together a plan tailored to your specific bruxism, muscle and TMJ pattern — no one-size-fits-all.",
  },
  {
    icon: Mail,
    title: "Proposal sent",
    body: "Your plan and transparent pricing arrive on WhatsApp. Take your time, ask anything — there's zero pressure to proceed.",
  },
  {
    icon: Sparkles,
    title: "Start treatment",
    body: "When you're ready, your first appointment is short, comfortable and has no downtime. Most patients head straight back to their day.",
  },
  {
    icon: Layers,
    title: "Combined, layered care",
    body: "We often layer custom splint therapy, NESA® neuromodulation and our in-clinic muscle-relaxing treatment for deeper, longer-lasting relief — explore the three modalities just below.",
    href: "#treatments",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how"
      data-testid="how-section"
      className="py-24 lg:py-32 bg-rb-bg-2 border-y border-rb-border relative overflow-hidden"
    >
      {/* Soft background atmosphere */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[20%] -right-32 w-[420px] h-[420px] rounded-full bg-[#93C5FD]/8 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-[380px] h-[380px] rounded-full bg-[#3B82F6]/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <div className="font-overline mb-4">Your journey with us</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-rb-text leading-[1.1]">
            From first message to{" "}
            <span className="italic text-[#93C5FD]">lasting relief</span>.
          </h2>
          <p className="mt-6 text-rb-text-2 leading-relaxed">
            Six clear steps. Honest at every stage. You're never locked in —
            the consultation is free and the plan is yours to think about
            before any treatment begins.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const Card = (
              <div className="relative card-soft p-7 h-full">
                <div
                  className="absolute -top-3 left-6 font-serif-display text-5xl italic text-[#93C5FD] leading-none select-none"
                  style={{ textShadow: "0 4px 24px rgba(15,27,44,0.6)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="h-10 w-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-5 mt-4">
                  <Icon size={18} strokeWidth={1.5} className="text-[#93C5FD]" />
                </div>
                <h3 className="font-serif-display text-xl text-rb-text mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-rb-text-2 leading-relaxed">
                  {s.body}
                </p>
                {s.href && (
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-[#93C5FD]">
                    Explore the treatments →
                  </span>
                )}
              </div>
            );
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative pt-6"
                data-testid={`how-step-${i}`}
              >
                {s.href ? (
                  <a href={s.href} className="block">{Card}</a>
                ) : (
                  Card
                )}
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-xs text-rb-text-2 max-w-2xl">
          Specific treatment options, including any prescription-only
          medicines, will only be discussed during your private consultation —
          in line with UK advertising rules.
        </p>
      </div>
    </section>
  );
};
